import { createAuthApi } from '../auth/createAuthApi';
import { API_PATHS } from '../constants/paths';
import { ApiStatusError } from '../errors/ApiStatusError';
import type { HttpClient, RequestOptions } from '../types/httpClient';
import type { AuthTokens, FetchHttpClientOptions } from '../types/transport';
import { isApiStatusError } from '../utils/isApiStatusError';

type SendOptions = Pick<FetchHttpClientOptions, 'baseUrl' | 'getHeaders' | 'timeoutMs'>;

async function sendRequest<T>(
    { baseUrl, getHeaders, timeoutMs }: SendOptions,
    path: string,
    options: RequestOptions,
    token?: string,
): Promise<T> {
    const url = new URL(`${baseUrl}${path}`);
    Object.entries(options.query ?? {}).forEach(([key, value]) => url.searchParams.set(key, value));

    const response = await fetch(url, {
        method: options.method ?? 'GET',
        headers: {
            // Without it Laravel treats the request as a page visit: a 401 becomes a 500 HTML page (pitfalls.md).
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...getHeaders?.(),
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        },
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
    });

    const text = await response.text();
    let body: unknown;
    try {
        body = text ? JSON.parse(text) : undefined;
    } catch {
        // Not JSON (e.g. a proxy's HTML error page): the status still tells what happened.
        body = text;
    }

    if (!response.ok) {
        throw new ApiStatusError(response.status, body);
    }

    return body as T;
}

/**
 * A browser transport over `fetch` (ADR 0002): sends the session token and, on a 401, refreshes it once (one shared
 * refresh for parallel requests) and repeats the request. Never sends server secrets.
 */
export function createFetchHttpClient(options: FetchHttpClientOptions): HttpClient {
    const { getTokens, onTokensRefreshed, onRefreshFailed, onError, shouldRefresh = true } = options;
    let refreshing: Promise<AuthTokens | null> | null = null;

    const refreshTokens = (): Promise<AuthTokens | null> => {
        if (!refreshing) {
            const refreshToken = getTokens?.()?.refresh_token;
            const bare: HttpClient = {
                request: (path, requestOptions) => sendRequest(options, path, requestOptions ?? {}),
            };

            refreshing = (refreshToken ? createAuthApi(bare).refresh(refreshToken) : Promise.resolve(null))
                .then(
                    (response) =>
                        response && { access_token: response.access_token, refresh_token: response.refresh_token },
                )
                .catch(() => null)
                .then((tokens) => {
                    if (tokens) {
                        onTokensRefreshed?.(tokens);
                    } else {
                        onRefreshFailed?.();
                    }

                    return tokens;
                })
                .finally(() => {
                    refreshing = null;
                });
        }

        return refreshing;
    };

    const fail = (err: unknown): never => {
        onError?.(err);
        throw err;
    };

    return {
        async request<T>(path: string, requestOptions: RequestOptions = {}): Promise<T> {
            const token = getTokens?.()?.access_token;

            try {
                return await sendRequest<T>(options, path, requestOptions, token);
            } catch (err) {
                if (!shouldRefresh || !token || path === API_PATHS.AUTH_TOKEN_REFRESH || !isApiStatusError(err, 401)) {
                    return fail(err);
                }

                const tokens = await refreshTokens();
                if (!tokens) {
                    // The session is over: `onRefreshFailed` has reported it, so no `onError`.
                    throw err;
                }

                return sendRequest<T>(options, path, requestOptions, tokens.access_token).catch(fail);
            }
        },
    };
}
